import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import type { ComponentType } from 'react';
import { X } from 'lucide-react';
import {
  installCalendarCreateGestureSwallow,
  isCalendarCreateTarget,
  swallowCalendarCreateEvent,
} from '../../hooks/useDismissOverlayProtectingCalendarCreate';
import {
  monthCreateSlotKey,
  type AnchorRect,
} from '../BookingTypeSelectionModal';

export type { AnchorRect };
export { monthCreateSlotKey };

export type CalendarCreateChoiceOption = {
  id: string;
  label: string;
  description: string;
  icon: ComponentType<{ className?: string; strokeWidth?: number }>;
  /** Render the icon as a full-tile image mark (official platform logos) instead of a glyph on blue. */
  imageIcon?: boolean;
  onSelect: () => void;
};

type Placement = {
  top: number;
  left: number;
  caretSide: 'left' | 'right' | 'top';
  transformOrigin: string;
};

const MENU_WIDTH = 300;
const MENU_GAP = 10;
const VIEWPORT_PAD = 12;
const ESTIMATED_MENU_HEIGHT = 280;

function resolveAnchorElement(slotKey: string | null | undefined): HTMLElement | null {
  if (!slotKey) return null;
  if (slotKey.startsWith('month:')) {
    const date = slotKey.slice(6);
    return (
      document.querySelector<HTMLElement>(`.fc-daygrid-day[data-date="${date}"]`) ||
      document.querySelector<HTMLElement>(`[data-date="${date}"]`)
    );
  }
  const escaped =
    typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
      ? CSS.escape(slotKey)
      : slotKey.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  return document.querySelector<HTMLElement>(`[data-calendar-create-slot="${escaped}"]`);
}

function rectFromElement(el: HTMLElement): AnchorRect {
  const rect = el.getBoundingClientRect();
  return {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
  };
}

function getCalendarChromeBottom(): number {
  let bottom = 0;

  const fixedHeader = document.querySelector('.calendar-fixed-header') as HTMLElement | null;
  if (fixedHeader) {
    bottom = Math.max(bottom, fixedHeader.getBoundingClientRect().bottom);
  }

  const gridHeader = document.querySelector(
    '[data-calendar-grid-header]',
  ) as HTMLElement | null;
  if (gridHeader) {
    const rect = gridHeader.getBoundingClientRect();
    if (rect.bottom > bottom && rect.top < bottom + 8) {
      bottom = Math.max(bottom, rect.bottom);
    } else if (!fixedHeader) {
      bottom = Math.max(bottom, rect.bottom);
    }
  }

  if (bottom > 0) return bottom;

  const nav =
    (document.querySelector('[data-app-navbar]') as HTMLElement | null) ||
    (document.querySelector('nav.fixed, header.fixed') as HTMLElement | null);
  if (nav) return nav.getBoundingClientRect().bottom;

  return 0;
}

function clipRectBelowChrome(rect: AnchorRect, chromeBottom: number): AnchorRect | null {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const top = Math.max(rect.top, chromeBottom + 2);
  const bottom = Math.min(rect.top + rect.height, vh - 4);
  const left = Math.max(rect.left, 0);
  const right = Math.min(rect.left + rect.width, vw);
  const height = bottom - top;
  const width = right - left;
  if (height < 10 || width < 8) return null;
  return { top, left, width, height };
}

function computePlacement(
  anchor: AnchorRect,
  menuHeight: number,
  chromeBottom = 0,
): Placement {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const minTop = Math.max(VIEWPORT_PAD, chromeBottom + 8);
  const height = Math.min(menuHeight || ESTIMATED_MENU_HEIGHT, vh - minTop - VIEWPORT_PAD);

  const spaceRight = vw - (anchor.left + anchor.width) - VIEWPORT_PAD;
  const spaceLeft = anchor.left - VIEWPORT_PAD;

  let caretSide: Placement['caretSide'] = 'left';
  let left: number;
  let transformOrigin: string;

  if (spaceRight >= MENU_WIDTH + MENU_GAP || spaceRight >= spaceLeft) {
    left = anchor.left + anchor.width + MENU_GAP;
    caretSide = 'left';
    transformOrigin = 'left center';
    if (left + MENU_WIDTH > vw - VIEWPORT_PAD) {
      left = Math.max(VIEWPORT_PAD, vw - VIEWPORT_PAD - MENU_WIDTH);
    }
  } else if (spaceLeft >= MENU_WIDTH + MENU_GAP) {
    left = anchor.left - MENU_GAP - MENU_WIDTH;
    caretSide = 'right';
    transformOrigin = 'right center';
  } else {
    left = anchor.left + anchor.width / 2 - MENU_WIDTH / 2;
    left = Math.max(VIEWPORT_PAD, Math.min(left, vw - VIEWPORT_PAD - MENU_WIDTH));
    caretSide = 'top';
    transformOrigin = 'top center';
  }

  let top: number;
  if (caretSide === 'top') {
    top = anchor.top + anchor.height + MENU_GAP;
    if (top + height > vh - VIEWPORT_PAD) {
      top = Math.max(minTop, anchor.top - MENU_GAP - height);
      transformOrigin = 'bottom center';
    }
  } else {
    top = anchor.top + anchor.height / 2 - height / 2;
    top = Math.max(minTop, Math.min(top, vh - VIEWPORT_PAD - height));
  }

  top = Math.max(minTop, top);
  return { top, left, caretSide, transformOrigin };
}

export default function CalendarCreateChoiceMenu({
  isOpen,
  onClose,
  options,
  contextLabel,
  footerNote,
  loading = false,
  anchorRect = null,
  anchorSlotKey = null,
}: {
  isOpen: boolean;
  onClose: () => void;
  options: CalendarCreateChoiceOption[];
  contextLabel?: string;
  footerNote?: string | null;
  loading?: boolean;
  anchorRect?: AnchorRect | null;
  anchorSlotKey?: string | null;
}) {
  const menuRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const [liveRect, setLiveRect] = useState<AnchorRect | null>(anchorRect);
  const [placement, setPlacement] = useState<Placement | null>(null);
  const [menuHeight, setMenuHeight] = useState(ESTIMATED_MENU_HEIGHT);

  const anchored = Boolean(liveRect && liveRect.width > 0 && liveRect.height > 0);

  useEffect(() => {
    if (!isOpen) {
      setLiveRect(null);
      return;
    }
    const chromeBottom = getCalendarChromeBottom();
    const fromKey = anchorSlotKey ? resolveAnchorElement(anchorSlotKey) : null;
    const raw = fromKey ? rectFromElement(fromKey) : anchorRect || null;
    setLiveRect(raw ? clipRectBelowChrome(raw, chromeBottom) : null);
  }, [isOpen, anchorRect, anchorSlotKey]);

  useEffect(() => {
    if (!isOpen || anchored) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, anchored]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  const syncToSlot = useCallback(() => {
    if (!anchorSlotKey && !anchorRect) {
      setLiveRect(null);
      setPlacement(null);
      return;
    }

    const chromeBottom = getCalendarChromeBottom();
    const fromKey = anchorSlotKey ? resolveAnchorElement(anchorSlotKey) : null;
    const raw = fromKey ? rectFromElement(fromKey) : anchorRect || null;

    if (!raw) {
      if (anchorSlotKey && !resolveAnchorElement(anchorSlotKey)) {
        onCloseRef.current();
      }
      return;
    }

    const clipped = clipRectBelowChrome(raw, chromeBottom);
    if (!clipped) {
      onCloseRef.current();
      return;
    }

    setLiveRect(clipped);
    setPlacement(computePlacement(clipped, menuHeight, chromeBottom));
  }, [anchorSlotKey, anchorRect, menuHeight]);

  useLayoutEffect(() => {
    if (!isOpen || !liveRect) return;
    setPlacement(computePlacement(liveRect, menuHeight, getCalendarChromeBottom()));
  }, [isOpen, liveRect, menuHeight]);

  useLayoutEffect(() => {
    if (!isOpen || !anchored || !menuRef.current) return;
    const h = menuRef.current.getBoundingClientRect().height;
    if (h > 0 && Math.abs(h - menuHeight) > 2) {
      setMenuHeight(h);
    }
  }, [isOpen, anchored, menuHeight, options.length, loading, footerNote, contextLabel]);

  useEffect(() => {
    if (!isOpen || !anchored) return;

    let raf = 0;
    const scheduleSync = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(syncToSlot);
    };

    window.addEventListener('scroll', scheduleSync, true);
    window.addEventListener('resize', scheduleSync);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', scheduleSync, true);
      window.removeEventListener('resize', scheduleSync);
    };
  }, [isOpen, anchored, syncToSlot]);

  useEffect(() => {
    if (!isOpen) return;

    const onOutsideDown = (e: Event) => {
      const target = e.target;
      if (!(target instanceof Node)) return;
      if (menuRef.current?.contains(target)) return;

      const onSlot = isCalendarCreateTarget(e.target);
      if (anchored) {
        if (onSlot) swallowCalendarCreateEvent(e);
        onCloseRef.current();
        return;
      }

      if (onSlot) {
        swallowCalendarCreateEvent(e);
        installCalendarCreateGestureSwallow();
      }
      onCloseRef.current();
    };

    document.addEventListener('pointerdown', onOutsideDown, true);
    document.addEventListener('mousedown', onOutsideDown, true);
    document.addEventListener('touchstart', onOutsideDown, true);

    return () => {
      document.removeEventListener('pointerdown', onOutsideDown, true);
      document.removeEventListener('mousedown', onOutsideDown, true);
      document.removeEventListener('touchstart', onOutsideDown, true);
    };
  }, [isOpen, anchored]);

  if (!isOpen) return null;

  const menuShellClass =
    'ios-create-popover w-[300px] max-w-[calc(100vw-24px)] overflow-hidden pointer-events-auto animate-create-popover-in';

  const menuShellStyle: React.CSSProperties = {
    borderRadius: 14,
    background: 'rgba(255, 255, 255, 0.78)',
    border: '0.5px solid rgba(0, 0, 0, 0.08)',
    boxShadow:
      '0 0 0 0.5px rgba(0,0,0,0.04), 0 10px 40px rgba(0,0,0,0.18), 0 2px 10px rgba(0,0,0,0.08)',
    backdropFilter: 'blur(40px) saturate(180%)',
    WebkitBackdropFilter: 'blur(40px) saturate(180%)',
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", system-ui, sans-serif',
  };

  const menuInner = (
    <>
      <div className="flex items-center gap-2 px-3.5 pt-3 pb-2.5">
        <div className="min-w-0 flex-1">
          <p
            className="text-[11px] font-semibold tracking-wide text-black/35"
            style={{ letterSpacing: '0.02em' }}
          >
            Create
          </p>
          {contextLabel ? (
            <p className="mt-1 text-[13px] text-black/55 truncate font-medium">{contextLabel}</p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-black/[0.06] text-black/40 hover:bg-black/[0.1] active:bg-black/[0.14] transition-colors"
          aria-label="Close"
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <X className="h-3.5 w-3.5" strokeWidth={2.5} />
        </button>
      </div>

      <div className="mx-2.5 mb-2.5 overflow-hidden rounded-[10px] bg-black/[0.035]">
        {loading && options.length === 0 ? (
          <p className="text-[13px] text-black/40 text-center py-5 px-3">Loading…</p>
        ) : (
          options.map((option, index) => {
            const Icon = option.icon;
            const isLast = index === options.length - 1;
            return (
              <button
                key={option.id}
                type="button"
                role="menuitem"
                onClick={() => {
                  option.onSelect();
                  onClose();
                }}
                className={`flex w-full items-center gap-3 px-3 py-2.5 text-left touch-manipulation transition-colors hover:bg-black/[0.04] active:bg-black/[0.08] animate-create-option-in ${
                  !isLast ? 'border-b border-black/[0.06]' : ''
                }`}
                style={{
                  WebkitTapHighlightColor: 'transparent',
                  animationDelay: `${50 + index * 40}ms`,
                }}
              >
                <span
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] overflow-hidden shadow-sm ${
                    option.imageIcon
                      ? 'bg-white border border-black/10'
                      : 'bg-[#007AFF] text-white shadow-[#007AFF]/30'
                  }`}
                >
                  {option.imageIcon ? (
                    <Icon className="h-8 w-8" />
                  ) : (
                    <Icon className="h-[15px] w-[15px]" strokeWidth={2.25} />
                  )}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[15px] font-semibold text-black/85 leading-tight">
                    {option.label}
                  </span>
                  <span className="block text-[12px] text-black/40 mt-0.5 leading-snug">
                    {option.description}
                  </span>
                </span>
              </button>
            );
          })
        )}
      </div>

      {footerNote ? (
        <p className="px-3.5 pb-3 text-[11px] text-black/35 leading-snug">{footerNote}</p>
      ) : null}
    </>
  );

  if (anchored && liveRect) {
    const chromeBottom = getCalendarChromeBottom();
    const pos = placement ?? computePlacement(liveRect, menuHeight, chromeBottom);

    return createPortal(
      <>
        <div
          className="fixed animate-create-backdrop-in pointer-events-none"
          style={{
            zIndex: 40,
            top: chromeBottom,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(15, 23, 42, 0.06)',
          }}
        />

        <div
          className="fixed pointer-events-none animate-create-slot-in"
          style={{
            zIndex: 40,
            top: liveRect.top,
            left: liveRect.left,
            width: Math.max(liveRect.width, 2),
            height: Math.max(liveRect.height, 2),
            borderRadius: 6,
            background: 'rgba(59, 130, 246, 0.16)',
            border: '1.5px dashed rgba(59, 130, 246, 0.65)',
            boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.12)',
          }}
        />

        <div
          ref={menuRef}
          role="menu"
          className={`fixed ${menuShellClass}`}
          style={{
            ...menuShellStyle,
            zIndex: 100160,
            top: pos.top,
            left: pos.left,
            maxHeight: `min(90vh, ${Math.max(120, window.innerHeight - pos.top - 12)}px)`,
            overflowY: 'auto',
            transformOrigin: pos.transformOrigin,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {pos.caretSide === 'left' && (
            <span
              aria-hidden
              className="absolute w-2.5 h-2.5 rotate-45"
              style={{
                left: -5,
                top: Math.min(
                  Math.max(liveRect.top + liveRect.height / 2 - pos.top - 5, 16),
                  menuHeight - 22,
                ),
                background: 'rgba(255, 255, 255, 0.86)',
                borderLeft: '0.5px solid rgba(0,0,0,0.08)',
                borderBottom: '0.5px solid rgba(0,0,0,0.08)',
              }}
            />
          )}
          {pos.caretSide === 'right' && (
            <span
              aria-hidden
              className="absolute w-2.5 h-2.5 rotate-45"
              style={{
                right: -5,
                top: Math.min(
                  Math.max(liveRect.top + liveRect.height / 2 - pos.top - 5, 16),
                  menuHeight - 22,
                ),
                background: 'rgba(255, 255, 255, 0.86)',
                borderRight: '0.5px solid rgba(0,0,0,0.08)',
                borderTop: '0.5px solid rgba(0,0,0,0.08)',
              }}
            />
          )}
          {pos.caretSide === 'top' && (
            <span
              aria-hidden
              className="absolute w-2.5 h-2.5 rotate-45"
              style={{
                top: -5,
                left: Math.min(
                  Math.max(liveRect.left + liveRect.width / 2 - pos.left - 5, 16),
                  MENU_WIDTH - 24,
                ),
                background: 'rgba(255, 255, 255, 0.86)',
                borderLeft: '0.5px solid rgba(0,0,0,0.08)',
                borderTop: '0.5px solid rgba(0,0,0,0.08)',
              }}
            />
          )}
          {menuInner}
        </div>
      </>,
      document.body,
    );
  }

  return createPortal(
    <>
      <div
        className="fixed inset-0 transition-opacity duration-200"
        style={{
          zIndex: 100150,
          background: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          opacity: 1,
          pointerEvents: 'auto',
        }}
        onClick={onClose}
      />
      <div
        className="fixed inset-0 flex items-center justify-center pointer-events-none px-4"
        style={{ zIndex: 100160 }}
      >
        <div
          ref={menuRef}
          role="menu"
          className={menuShellClass}
          style={{
            ...menuShellStyle,
            maxHeight: '90vh',
            overflowY: 'auto',
            transformOrigin: 'center center',
            width: '100%',
            maxWidth: 300,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {menuInner}
        </div>
      </div>
    </>,
    document.body,
  );
}
